import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { resetPasswordWithToken } from "../lib/api";

/**
 * Page publique de reset password (Sprint Y-2).
 *
 * Accessible via le lien magic-link envoye par email :
 *   /reset?token=<hex64>
 *
 * L'utilisateur saisit son nouveau mot de passe (min 10 chars). Apres succes,
 * redirige vers /login pour qu'il se reconnecte avec le nouveau mdp.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mut = useMutation({
    mutationFn: () => resetPasswordWithToken({ token, password }),
    onSuccess: () => {
      setDone(true);
      window.setTimeout(() => navigate("/login", { replace: true }), 1500);
    },
    onError: (e: Error) => setError(e.message),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("Lien invalide : token manquant.");
      return;
    }
    if (password.length < 10) {
      setError("Le mot de passe doit faire au moins 10 caracteres.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    mut.mutate();
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-[color:var(--color-alto-border)] rounded-2xl p-8 shadow-sm text-center">
          <p className="text-sm text-[color:var(--color-alto-accent)]">
            Lien invalide : aucun token dans l'URL.
          </p>
          <Link
            to="/login"
            className="block mt-4 text-sm text-[color:var(--color-alto-muted)] hover:text-[color:var(--color-alto-ink)] underline"
          >
            ← Retour a la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white border border-[color:var(--color-alto-border)] rounded-2xl p-8 shadow-sm">
        <div className="mb-6">
          <p className="text-xs tracking-widest text-[color:var(--color-alto-muted)] uppercase">
            ALTO ONSITE
          </p>
          <h1 className="font-[family-name:var(--font-display)] text-2xl mt-1">
            Nouveau mot de passe
          </h1>
          <p className="text-sm text-[color:var(--color-alto-muted)] mt-2">
            Choisissez un mot de passe d'au moins 10 caractères.
          </p>
        </div>

        {done ? (
          <p className="text-sm text-emerald-700">
            ✓ Mot de passe mis à jour. Redirection vers la connexion…
          </p>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium mb-1.5">
                Nouveau mot de passe
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                autoFocus
                required
                minLength={10}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--color-alto-accent)] focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="confirm" className="block text-sm font-medium mb-1.5">
                Confirmation
              </label>
              <input
                id="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={10}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-[color:var(--color-alto-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--color-alto-accent)] focus:border-transparent"
              />
            </div>

            {error && (
              <p className="text-sm text-[color:var(--color-alto-accent)]" role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={mut.isPending || !password || !confirm}
              className="w-full px-4 py-2.5 rounded-md bg-[color:var(--color-alto-ink)] text-white font-medium transition-opacity disabled:opacity-50 hover:opacity-90"
            >
              {mut.isPending ? "Mise à jour…" : "Définir le mot de passe"}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-xs text-[color:var(--color-alto-muted)] hover:text-[color:var(--color-alto-ink)] underline"
              >
                ← Retour a la connexion
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
